import React, { useEffect, useState } from 'react';
import CatalogView from './components/CatalogView';
import ShelfView from './components/ShelfView';
import ProductDetailPanel from './components/ProductDetailPanel';
import FloatingBasket from './components/FloatingBasket';
import OrderConfirmationModal from './components/OrderConfirmationModal';
import SearchOverlay from './components/SearchOverlay';
import KioskLayout from './components/KioskLayout';
import DashboardReturnBar from './components/DashboardReturnBar';
import { useBasketStore } from './state/basketStore';
import { fetchCategories } from './lib/api';
import { getCategoryIcon } from '../../../packages/rcc-design/src/CategoryIcons';
import { getOrgId, getCatalogId, getContext } from './utils/tenantBoot';

/**
 * Check if running in kiosk mode
 */
const isKioskMode = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('kiosk') === '1';
};

/**
 * PWA-style SPA Catalog App
 * Features:
 * - Sticky product detail panel (top 25%-33%)
 * - Scrollable catalog grid underneath
 * - No page navigation, instant product switching
 * - Floating basket button
 * - Order confirmation modal
 */
function App() {
  console.log('[App] Component rendering');
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSelectionSeq, setProductSelectionSeq] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showBasket, setShowBasket] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedFilters, setExpandedFilters] = useState(new Set()); // For Brand expansion
  const [brands, setBrands] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [customIcons, setCustomIcons] = useState([]); // Custom uploaded category icons
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedProductGroup, setSelectedProductGroup] = useState(null);
  const [orderCode, setOrderCode] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // Default, will be loaded from backend
  const [variantDisplay, setVariantDisplay] = useState(
    localStorage.getItem('catalogVariantDisplay') || 'grouped'
  ); // 'grouped' or 'expanded'
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [catalogSettings, setCatalogSettings] = useState({
    show_stock_availability: true,
    low_stock_threshold: 10
  });
  const clearBasket = useBasketStore(state => state.clearBasket);
  const clearOtherCatalogs = useBasketStore(state => state.clearOtherCatalogs);

  const baseUrl = import.meta.env.VITE_RCC_BASE || '';
  const orgId = getOrgId();

  // Clean basket from other catalogs on mount
  useEffect(() => {
    clearOtherCatalogs();
  }, [clearOtherCatalogs]);

  // Load categories for toolbar
  useEffect(() => {
    console.log('[App] useEffect running - loading categories, brands, product groups, and custom icons');
    console.log('[App] baseUrl:', baseUrl);
    loadCategories();
    loadBrands();
    loadProductGroups();
    loadCustomIcons();
  }, []);

  const loadCategories = async () => {
    try {
      const mode = isKioskMode() ? 'catalog' : 'ecommerce';
      const data = await fetchCategories(mode);
      setCategories(data || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadBrands = async () => {
    try {
      // Kiosk mode: scope brands to the current screen's resolved catalogue so
      // a kiosk never lists brands belonging only to a different catalogue.
      const catalogId = isKioskMode() ? getCatalogId() : null;
      const catalogParam = catalogId ? `&catalog_id=${catalogId}` : '';
      console.log('[App] Loading brands from:', `${baseUrl}/rcc-api/brands?active_only=true&org_id=${orgId}&has_products=true${catalogParam}`);
      const response = await fetch(`${baseUrl}/rcc-api/brands?active_only=true&org_id=${orgId}&has_products=true${catalogParam}`);
      console.log('[App] Brands response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[App] Loaded brands:', data);
        setBrands(data.brands || []);
      } else {
        const errorText = await response.text();
        console.error('[App] Failed to load brands:', response.status, errorText);
      }
    } catch (err) {
      console.error('[App] Error loading brands:', err);
    }
  };

  const loadProductGroups = async () => {
    try {
      console.log('[App] Loading product groups from:', `${baseUrl}/rcc-api/product-groups/definitions?org_id=${orgId}&has_products=true`);
      const response = await fetch(`${baseUrl}/rcc-api/product-groups/definitions?org_id=${orgId}&has_products=true`);
      console.log('[App] Product groups response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[App] Loaded product groups:', data);
        // Only show manual groups that have products assigned
        const manualGroups = (data.groups || []).filter(g => g.group_type === 'manual');
        console.log('[App] Manual groups:', manualGroups);
        setProductGroups(manualGroups);
      } else {
        const errorText = await response.text();
        console.error('[App] Failed to load product groups:', response.status, errorText);
      }
    } catch (err) {
      console.error('[App] Error loading product groups:', err);
    }
  };

  const loadCustomIcons = async () => {
    try {
      console.log('[App] Loading custom icons from:', `${baseUrl}/rcc-api/category-icons?org_id=${orgId}`);
      const response = await fetch(`${baseUrl}/rcc-api/category-icons?org_id=${orgId}`);
      console.log('[App] Custom icons response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[App] Loaded custom icons:', data);
        setCustomIcons(data || []);
      } else {
        const errorText = await response.text();
        console.error('[App] Failed to load custom icons:', response.status, errorText);
      }
    } catch (err) {
      console.error('[App] Error loading custom icons:', err);
    }
  };

  // Load settings from backend on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch(`${baseUrl}/rcc-api/catalog/settings?org_id=${orgId}`);
        if (response.ok) {
          const data = await response.json();
          console.log('[App] Loaded catalog settings:', data);
          
          if (data.success && data.settings) {
            const s = data.settings;
            setViewMode(s.view_mode || 'grid');
            
            // Store stock availability settings
            setCatalogSettings({
              show_stock_availability: s.show_stock_availability !== false,
              low_stock_threshold: s.low_stock_threshold || 10
            });
            
            // Store scroll settings for ShelfView to read
            window.__CATALOG_SETTINGS__ = {
              auto_scroll_interval: s.auto_scroll_interval || 2000,
              idle_timeout: s.idle_timeout || 10000,
            };
          }
        }
      } catch (err) {
        console.error('[App] Error loading settings:', err);
      } finally {
        setLoadingSettings(false);
      }
    };

    loadSettings();
  }, []);

  // Toggle variant display mode
  const toggleVariantDisplay = () => {
    const newMode = variantDisplay === 'grouped' ? 'expanded' : 'grouped';
    setVariantDisplay(newMode);
    localStorage.setItem('catalogVariantDisplay', newMode);
  };

  useEffect(() => {
    // Apply kiosk mode class to body
    if (isKioskMode()) {
      document.body.classList.add('kiosk-mode');
    } else {
      document.body.classList.remove('kiosk-mode');
    }
  }, []);

  const handleProductSelect = (product) => {
    // Only force a fresh ProductDetailPanel mount when RE-selecting the same
    // product that's already open (so a variant switched to on a previous
    // view never leaks into the new open - see key= on ProductDetailPanel
    // below). Selecting a genuinely different product already gets a fresh
    // mount from the product_id changing in that key, so bumping the seq
    // here too was causing an extra reset/refetch and a visible flicker on
    // every single selection instead of only on same-product re-opens.
    if (selectedProduct?.product_id === product.product_id) {
      setProductSelectionSeq(seq => seq + 1);
    }
    setSelectedProduct(product);
  };

  const handleCloseProductPanel = () => {
    setSelectedProduct(null);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    // Every filter (Category, Brand, Product Group) combines with whichever
    // others are active - none of them replace another. Selecting this one
    // must not clear the rest.
    // Don't clear product - let panel stay visible while browsing
  };

  // Reset to just ONE filter - used by the combined-filter breadcrumb, where
  // clicking any single segment (whichever filter type it represents) drops
  // every other active filter and fetches only what was clicked.
  const handleFilterOnlySelect = (type, value) => {
    setSelectedCategory(type === 'category' ? value : null);
    setSelectedBrand(type === 'brand' ? value : null);
    setSelectedProductGroup(type === 'group' ? value : null);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    // Don't clear product - let panel stay visible
  };

  const handleOrderComplete = (code) => {
    setOrderCode(code);
    setShowBasket(false);
  };

  const handleOrderConfirmDone = () => {
    setOrderCode(null);
    clearBasket();
    setSelectedProduct(null);
    setSelectedCategory(null);
  };

  if (loadingSettings) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#64748b' 
      }}>
        Loading catalog...
      </div>
    );
  }

  return (
    <KioskLayout
      idleTimeoutMs={30000}
      campaignHeightPercent={75}
    >
      <DashboardReturnBar />
      <div className={`app-container ${selectedProduct ? 'has-product-panel' : ''}`}>
        {/* Vertical Control Strip - Left side, below product panel when open */}
        <div className={`vertical-control-strip ${selectedProduct ? 'with-product-panel' : ''}`}>
          {/* Search Button */}
          <button 
            className="control-btn search-btn" 
            onClick={() => setShowSearch(true)}
            title="Search products"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          {/* View Mode Toggle - Grid */}
          <button 
            className={`control-btn view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid View"
          >
            ⊞
          </button>
          
          {/* View Mode Toggle - Shelf */}
          <button 
            className={`control-btn view-mode-btn ${viewMode === 'shelf' ? 'active' : ''}`}
            onClick={() => setViewMode('shelf')}
            title="Shelf View"
          >
            ☰
          </button>
          
          {/* Variant Display Toggle - Only show in grid mode */}
          {viewMode === 'grid' && (
            <button 
              className={`control-btn variant-btn ${variantDisplay === 'grouped' ? 'active' : ''}`}
              onClick={toggleVariantDisplay}
              title={variantDisplay === 'grouped' ? 'Show all variants (expanded)' : 'Group variants'}
            >
              {variantDisplay === 'grouped' ? '📦' : '📋'}
            </button>
          )}
          {/* Category Button */}
          <button 
            className={`control-btn category-btn ${showCategories ? 'active' : ''}`}
            onClick={() => {
              setShowCategories(!showCategories);
              setShowFilters(false);
            }}
            title="Categories"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
            </svg>
          </button>

          {/* Filter Button */}
          <button 
            className={`control-btn filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => {
              console.log('[App] Filter button clicked! Current showFilters:', showFilters);
              console.log('[App] Brands state:', brands);
              console.log('[App] Product groups state:', productGroups);
              setShowFilters(!showFilters);
              setShowCategories(false);
            }}
            title="Filters (Brands & Groups)"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
          </button>
        </div>

        {/* Category Dropdown */}
        {showCategories && (
          <div className="category-dropdown">
            <div className="category-dropdown-header">
              <span>Categories</span>
              <button onClick={() => setShowCategories(false)} className="dropdown-close">×</button>
            </div>
            <div className="category-list">
              <button
                className={`category-item ${!selectedCategory ? 'active' : ''}`}
                onClick={() => {
                  handleBackToCategories();
                  setSelectedBrand(null);
                  setSelectedProductGroup(null);
                  setShowCategories(false);
                }}
              >
                All Products
              </button>
              {categories.filter(cat => !cat.parent_id).map(category => {
                const subcategories = categories.filter(c => c.parent_id === category.category_id);
                const isExpanded = expandedCategories.has(category.category_id);
                
                const toggleExpand = (e) => {
                  e.stopPropagation();
                  const newExpanded = new Set(expandedCategories);
                  if (isExpanded) {
                    newExpanded.delete(category.category_id);
                  } else {
                    newExpanded.add(category.category_id);
                  }
                  setExpandedCategories(newExpanded);
                };
                
                return (
                  <div key={category.category_id} className="category-group">
                    <button
                      className={`category-item ${selectedCategory?.category_id === category.category_id ? 'active' : ''}`}
                      onClick={() => {
                        if (subcategories.length > 0) {
                          // Parent with children: this click is only for revealing
                          // subcategories - it must never apply a filter or close
                          // the menu until the customer picks an actual leaf.
                          const newExpanded = new Set(expandedCategories);
                          if (isExpanded) {
                            newExpanded.delete(category.category_id);
                          } else {
                            newExpanded.add(category.category_id);
                          }
                          setExpandedCategories(newExpanded);
                          return;
                        }
                        handleCategorySelect(category);
                        setShowCategories(false);
                      }}
                    >
                      <span className="category-icon">
                        {(() => {
                          const customIcon = customIcons.find(ic => ic.icon_key === category.icon_name);
                          if (customIcon) {
                            let svgContent = customIcon.svg_content;
                            if (!svgContent.includes('width=') && !svgContent.includes('height=')) {
                              // Preserve viewBox if present, otherwise add default viewBox
                              if (svgContent.includes('viewBox=')) {
                                svgContent = svgContent.replace('<svg', '<svg width="20" height="20"');
                              } else {
                                svgContent = svgContent.replace('<svg', '<svg width="20" height="20" viewBox="0 0 20 20"');
                              }
                            }
                            return <span dangerouslySetInnerHTML={{ __html: svgContent }} />;
                          }
                          return getCategoryIcon(category.icon_name, category.name);
                        })()}
                      </span>
                      {category.name}
                      {subcategories.length > 0 && (
                        <span className="subcategory-indicator" onClick={toggleExpand}>
                          {isExpanded ? '−' : '+'}
                        </span>
                      )}
                    </button>
                    {subcategories.length > 0 && isExpanded && (
                      <div className="subcategory-list">
                        {subcategories.map(subcat => (
                          <button
                            key={subcat.category_id}
                            className={`subcategory-item ${selectedCategory?.category_id === subcat.category_id ? 'active' : ''}`}
                            onClick={() => {
                              handleCategorySelect(subcat);
                              setShowCategories(false);
                            }}
                          >
                            <span className="category-icon">
                              {(() => {
                                const customIcon = customIcons.find(ic => ic.icon_key === subcat.icon_name);
                                if (customIcon) {
                                  let svgContent = customIcon.svg_content;
                                  if (!svgContent.includes('width=') && !svgContent.includes('height=')) {
                                    // Preserve viewBox if present
                                    if (svgContent.includes('viewBox=')) {
                                      svgContent = svgContent.replace('<svg', '<svg width="20" height="20"');
                                    } else {
                                      svgContent = svgContent.replace('<svg', '<svg width="20" height="20" viewBox="0 0 20 20"');
                                    }
                                  }
                                  return <span dangerouslySetInnerHTML={{ __html: svgContent }} />;
                                }
                                return getCategoryIcon(subcat.icon_name, subcat.name);
                              })()}
                            </span>
                            {subcat.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Filter Dropdown (Brands & Product Groups) */}
        {showFilters && (
          <div className="category-dropdown filter-dropdown">
            <div className="category-dropdown-header">
              <span>Filters</span>
              <button onClick={() => setShowFilters(false)} className="dropdown-close">×</button>
            </div>
            <div className="category-list">
              {(() => {
                console.log('[App] Rendering filters - brands:', brands.length, 'productGroups:', productGroups.length);
                return null;
              })()}
              
              {/* Clear Filters */}
              <button
                className={`category-item ${!selectedBrand && !selectedProductGroup ? 'active' : ''}`}
                onClick={() => {
                  setSelectedBrand(null);
                  setSelectedProductGroup(null);
                  setSelectedCategory(null);
                  setShowFilters(false);
                }}
              >
                All Products
              </button>

              {/* Brands Section - Expandable */}
              {brands.length > 0 && (
                <div className="category-group">
                  <button
                    className="category-item filter-parent"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newExpanded = new Set(expandedFilters);
                      if (expandedFilters.has('brands')) {
                        newExpanded.delete('brands');
                      } else {
                        newExpanded.add('brands');
                      }
                      setExpandedFilters(newExpanded);
                    }}
                  >
                    Brand
                    <span className="subcategory-indicator">
                      {expandedFilters.has('brands') ? '−' : '+'}
                    </span>
                  </button>
                  {expandedFilters.has('brands') && (
                    <div className="subcategory-list">
                      {brands.map(brand => (
                        <button
                          key={brand.brand_id}
                          className={`subcategory-item filter-item ${selectedBrand?.brand_id === brand.brand_id ? 'active' : ''}`}
                          onClick={() => {
                            console.log('[App] Brand clicked:', brand.name);
                            setSelectedBrand(brand);
                            // Every filter (Category, Brand, Product Group)
                            // combines with whichever others are active -
                            // none of them replace another.
                            setShowFilters(false);
                          }}
                        >
                          {brand.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Product Groups Section - Direct items */}
              {productGroups.length > 0 && (
                <>
                  {productGroups.map(group => (
                    <button
                      key={group.group_id}
                      className={`category-item filter-item ${selectedProductGroup?.group_id === group.group_id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedProductGroup(group);
                        // Every filter (Category, Brand, Product Group)
                        // combines with whichever others are active - none
                        // of them replace another.
                        setShowFilters(false);
                      }}
                    >
                      {group.group_name}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
        {/* Search Overlay */}
        <SearchOverlay
          isOpen={showSearch}
          onClose={() => setShowSearch(false)}
          onProductSelect={handleProductSelect}
        />

        {/* Product Detail Panel - Sticky at top when product selected */}
        {selectedProduct && (
          <ProductDetailPanel
            key={`${selectedProduct.product_id}-${productSelectionSeq}`}
            product={selectedProduct}
            onClose={handleCloseProductPanel}
            catalogSettings={catalogSettings}
          />
        )}

        {/* Main Catalog View - Conditional rendering based on viewMode */}
        {viewMode === 'grid' ? (
          <CatalogView
            selectedCategory={selectedCategory}
            selectedBrand={selectedBrand}
            selectedProductGroup={selectedProductGroup}
            onCategorySelect={handleCategorySelect}
            onFilterOnlySelect={handleFilterOnlySelect}
            onProductSelect={handleProductSelect}
            onBackToCategories={handleBackToCategories}
            hasProductPanelOpen={!!selectedProduct}
            variantDisplay={variantDisplay}
          />
        ) : (
          <ShelfView
            selectedCategory={selectedCategory}
            selectedBrand={selectedBrand}
            selectedProductGroup={selectedProductGroup}
            onProductSelect={handleProductSelect}
          />
        )}

        {/* Floating Basket Button */}
        <FloatingBasket
          onToggle={() => setShowBasket(!showBasket)}
          showBasket={showBasket}
          onOrderComplete={handleOrderComplete}
        />

        {/* Order Confirmation Modal */}
        {orderCode && (
          <OrderConfirmationModal
            code={orderCode}
            onDone={handleOrderConfirmDone}
          />
        )}
      </div>
    </KioskLayout>
  );
}

export default App;
